import { resolveRegionalDestination, type PlannerRegion } from "./regional-destination";

const MAX_GROUP_ORDERS = 100;
const MAX_ENCODED_URL_LENGTH = 8_000;

export type BulkPickDestination =
  | { readonly available: true; readonly url: string }
  | { readonly available: false; readonly reason: string };

export function buildBulkPickDestination(
  baseUrl: string,
  pickingJobIds: readonly string[],
  plannerOrigin: string,
  localDevelopmentRegion?: PlannerRegion,
): BulkPickDestination {
  const regionalDestination = resolveRegionalDestination(plannerOrigin, localDevelopmentRegion);
  if (regionalDestination === null) {
    return { available: false, reason: "The BoostWare Bulk Pick URL is unavailable." };
  }

  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    return { available: false, reason: "The BoostWare Bulk Pick URL is unavailable." };
  }
  const expectedUrl = new URL(regionalDestination.bulkPickBaseUrl);
  if (
    url.origin !== expectedUrl.origin ||
    url.pathname !== expectedUrl.pathname ||
    url.username !== "" ||
    url.password !== "" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    return { available: false, reason: "The BoostWare Bulk Pick URL is unavailable." };
  }
  if (pickingJobIds.length === 0 || pickingJobIds.length > MAX_GROUP_ORDERS) {
    return {
      available: false,
      reason: "This group cannot be opened in Bulk Pick. Copy the order numbers instead.",
    };
  }
  if (
    pickingJobIds.some((id) => id.trim().length === 0 || id.includes(",")) ||
    new Set(pickingJobIds).size !== pickingJobIds.length
  ) {
    return {
      available: false,
      reason: "This group cannot be opened in Bulk Pick. Generate a new plan and try again.",
    };
  }
  url.searchParams.set("pickingJobIds", pickingJobIds.join(","));
  const destination = url.toString();
  if (destination.length > MAX_ENCODED_URL_LENGTH) {
    return {
      available: false,
      reason: "This order list is too long to open safely. Copy the order numbers instead.",
    };
  }
  return { available: true, url: destination };
}
